import type { JSX, ReactNode } from 'react';
import { useId } from 'react';

import { Button } from './Button.js';
import { Modal } from './Modal.js';
import { Heading } from './typography/Heading.js';
import { Text } from './typography/Text.js';

export interface ConfirmDialogProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** Called when the dialog should close without confirming (Cancel / Escape / overlay). */
  onCancel: () => void;
  /** Called when the user confirms the action. */
  onConfirm: () => void;
  /** Dialog title shown as heading. */
  title: string;
  /** Body copy or custom content describing the action. */
  children: ReactNode;
  /** Label for the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * When true, styles the confirm button as a destructive action.
   * Defaults to false (primary confirm).
   */
  destructive?: boolean;
  /** Marks confirm as busy (disables both buttons, aria-busy on confirm). */
  busy?: boolean;
}

/**
 * Accessible confirm/cancel dialog built on {@link Modal}.
 *
 * Use for destructive or irreversible actions (delete, retire, discard changes).
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={open}
 *   title="Delete survey"
 *   destructive
 *   confirmLabel="Delete"
 *   onCancel={() => setOpen(false)}
 *   onConfirm={handleDelete}
 * >
 *   This will retire the survey. You can restore it later by editing status.
 * </ConfirmDialog>
 * ```
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
}: ConfirmDialogProps): JSX.Element {
  const titleId = useId();

  return (
    <Modal open={open} onClose={onCancel} aria-labelledby={titleId}>
      <Heading as="h2" size="5" id={titleId}>
        {title}
      </Heading>
      <div className="mt-3">
        {typeof children === 'string' ? (
          <Text as="p" size="2" color="muted">
            {children}
          </Text>
        ) : (
          children
        )}
      </div>
      <Modal.Footer>
        <Button tone="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button tone={destructive ? 'danger' : 'primary'} onClick={onConfirm} busy={busy}>
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
